/**
 * Instanced island rendering with PER-INSTANCE color variation.
 *
 * Blanket biome tints read as a filter; real kits vary per part. Every
 * island placement (cliff blocks, cap tiles, vegetation) becomes an
 * instance in an InstancedMesh per (piece, submesh), and every instance
 * gets its own color via setColorAt (multiplied over the kit texture):
 *
 * - blocks: geological STRATA (each course below the top drifts darker and
 *   cooler), per-block hue/value jitter keyed by a position hash, MOSS
 *   accents on the top course (forest/meadow), RUST mottling (barren).
 * - caps: hue/value spread around the grass tone + a subtle (x+z) parity
 *   alternation so meadows read as tended fields.
 * - vegetation: each ISLAND draws a foliage hue lean (some islands run
 *   autumn-warm, some deep green), each tree jitters around it. Foliage
 *   materials are detected by green dominance; trunks/rocks get value
 *   jitter only.
 *
 * All variation is deterministic from world position + island id — pure
 * presentation, no RNG streams touched. One draw call per (piece, submesh,
 * island) instead of one per placement.
 *
 * The spawn/exit waves animate instances individually: each InstancedMesh
 * carries `userData.animInstances` (base matrices + world positions) that
 * streamAnim.ts consumes.
 */
import * as THREE from "three";
import type { Placement } from "../shared/types.ts";
import type { Assets } from "./assets.ts";
import type { Island } from "../gen/endless/islands.ts";

export interface InstancedBuild {
  meshes: THREE.InstancedMesh[];
  /** Dispose per-build GPU state (instance buffers, cloned materials). */
  dispose(): void;
}

/** Per-instance animation data consumed by streamAnim. */
export interface AnimInstances {
  base: THREE.Matrix4[];
  worldPos: THREE.Vector3[];
}

interface SubMesh {
  geometry: THREE.BufferGeometry;
  material: THREE.Material;
  local: THREE.Matrix4;
  foliage: boolean;
}

const tmpM = new THREE.Matrix4();
const tmpQ = new THREE.Quaternion();
const tmpS = new THREE.Vector3();
const tmpP = new THREE.Vector3();
const tmpC = new THREE.Color();

/** Deterministic hash of a world position (+salt) → [0, 1). */
function hash01(x: number, y: number, z: number, salt: number): number {
  let h = Math.imul((x * 4 + 1024) | 0, 374761393) ^
    Math.imul((y * 4 + 2048) | 0, 668265263) ^
    Math.imul((z * 4 + 4096) | 0, 2246822519) ^
    Math.imul(salt | 0, 973913579);
  h = Math.imul(h ^ (h >>> 13), 3266489917);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

/** Submeshes of a template with their root-relative transforms. */
function subMeshesOf(template: THREE.Object3D): SubMesh[] {
  const out: SubMesh[] = [];
  const walk = (node: THREE.Object3D, parent: THREE.Matrix4) => {
    node.updateMatrix();
    const local = node === template
      ? new THREE.Matrix4()
      : new THREE.Matrix4().multiplyMatrices(parent, node.matrix);
    const mesh = node as THREE.Mesh;
    if (mesh.isMesh) {
      const mat = mesh.material as THREE.MeshStandardMaterial;
      const foliage = Boolean(
        mat.color && mat.color.g > mat.color.r * 1.15 &&
          mat.color.g > mat.color.b * 1.15,
      );
      out.push({
        geometry: mesh.geometry,
        material: mat,
        local,
        foliage,
      });
    }
    for (const child of node.children) walk(child, local);
  };
  walk(template, new THREE.Matrix4());
  return out;
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}

/** Color for a mass block: strata + jitter + biome accents (multiplied
 * over the cliff texture, so values hover near white). */
function blockColor(
  biome: string,
  depthBelowTop: number,
  x: number,
  y: number,
  z: number,
): THREE.Color {
  if (biome === "abstract") {
    // Dev-map blocking: saturated orange masses with grey courses — the
    // TF2-orange homage. Parity + hash decide which blocks go grey.
    const grey = hash01(x, y, z, 97) < 0.28;
    if (grey) {
      return tmpC.setHSL(0.08, 0.04, 0.72 + (hash01(x, y, z, 101) - 0.5) * 0.1)
        .clone();
    }
    return tmpC.setHSL(
      0.066 + (hash01(x, y, z, 103) - 0.5) * 0.015,
      0.85,
      0.55 + (hash01(x, y, z, 107) - 0.5) * 0.08,
    ).clone();
  }
  const band = Math.max(0, Math.floor(depthBelowTop));
  let h = 0.075 + band * 0.012 + (hash01(x, y, z, 11) - 0.5) * 0.05;
  let s = 0.06 + band * 0.02;
  let l = 0.88 - band * 0.055 + (hash01(x, y, z, 23) - 0.5) * 0.12;

  const accent = hash01(x, y, z, 37);
  if ((biome === "forest" || biome === "meadow") && depthBelowTop <= 1.5) {
    // Moss creeps over the top course.
    if (accent < 0.22) {
      h = 0.3 + (accent - 0.11) * 0.3;
      s = 0.3;
      l = clamp(l + 0.02, 0.6, 0.95);
    }
  } else if (biome === "barren" && accent < 0.3) {
    // Rust mottling.
    h = 0.055 + accent * 0.05;
    s = 0.28;
    l = clamp(l - 0.04, 0.5, 0.95);
  } else if (biome === "winter") {
    // Cold desaturated strata; snow creeps over the top course.
    h = 0.58;
    s = clamp(s * 0.5, 0, 0.1);
    l = clamp(l + 0.03, 0.6, 0.98);
    if (depthBelowTop <= 1.5 && accent < 0.35) {
      s = 0.08;
      l = 0.96;
    }
  } else if (biome === "spooky") {
    // Graveyard gloom: darker, cooler, drained.
    h = 0.62;
    s = clamp(s * 0.6, 0, 0.12);
    l = clamp(l - 0.12, 0.42, 0.85);
  } else if (biome === "tropical") {
    // Sun-warmed sandstone strata.
    h = 0.1 + (hash01(x, y, z, 113) - 0.5) * 0.02;
    s = clamp(0.16 + band * 0.02, 0, 0.3);
    l = clamp(l + 0.02, 0.6, 0.98);
  } else if (biome === "autumn" && accent < 0.4) {
    // Soft ochre mottling.
    h = 0.07 + accent * 0.04;
    s = 0.22;
    l = clamp(l - 0.02, 0.55, 0.95);
  }
  return tmpC.setHSL(h, clamp(s, 0, 0.6), clamp(l, 0.5, 0.98)).clone();
}

/** Color for a cap tile: grass spread + field parity. Themed biomes
 * reuse the nature 1×1 ground tile and repaint it here (M18a): winter
 * caps read as snow, tropical caps as sand. */
function capColor(biome: string, x: number, z: number): THREE.Color {
  const parity = (Math.abs(x + z) | 0) % 2 === 0 ? 0.025 : -0.025;
  if (biome === "winter") {
    const l = 0.955 + (hash01(x, 0, z, 47) - 0.5) * 0.04 + parity;
    return tmpC.setHSL(0.58, 0.07, clamp(l, 0.88, 0.99)).clone();
  }
  if (biome === "tropical") {
    const l = 0.86 + (hash01(x, 0, z, 47) - 0.5) * 0.1 + parity;
    return tmpC.setHSL(
      0.115 + (hash01(x, 0, z, 41) - 0.5) * 0.02,
      0.34,
      clamp(l, 0.7, 0.95),
    ).clone();
  }
  const warm = biome === "meadow" ? 0.035 : 0;
  const h = 0.29 + warm + (hash01(x, 0, z, 41) - 0.5) * 0.07;
  const s = 0.16 + hash01(x, 0, z, 43) * 0.18;
  let l = 0.88 + (hash01(x, 0, z, 47) - 0.5) * 0.12;
  l += parity;
  return tmpC.setHSL(h, s, clamp(l, 0.55, 0.98)).clone();
}

/** Color for vegetation submeshes: island-wide foliage lean + per-plant
 * jitter on foliage; value-only jitter elsewhere. */
function decorColor(
  islandLean: number,
  foliage: boolean,
  x: number,
  y: number,
  z: number,
): THREE.Color {
  if (foliage) {
    const h = 0.3 + islandLean + (hash01(x, y, z, 53) - 0.5) * 0.06;
    const s = 0.25 + hash01(x, y, z, 59) * 0.2;
    const l = 0.85 + (hash01(x, y, z, 61) - 0.5) * 0.16;
    // Autumn lean pushes hue below green — clamp into a leaf-plausible arc.
    return tmpC.setHSL(clamp(h, 0.06, 0.42), s, clamp(l, 0.55, 0.98)).clone();
  }
  const l = 0.9 + (hash01(x, y, z, 67) - 0.5) * 0.14;
  return tmpC.setHSL(0.08, 0.05, clamp(l, 0.6, 0.98)).clone();
}

/**
 * Build one island's placements as instanced meshes with per-instance
 * colors, appended directly to `group` (flat — the spawn/exit waves treat
 * each InstancedMesh as a bundle of individually-animated instances).
 */
export function buildIslandInstanced(
  island: Island,
  assets: Assets,
  group: THREE.Group,
  missing: Set<string>,
): InstancedBuild {
  // Bucket placements by pieceId.
  const byPiece = new Map<string, Placement[]>();
  for (const p of island.placements) {
    const list = byPiece.get(p.pieceId) ?? [];
    list.push(p);
    byPiece.set(p.pieceId, list);
  }

  // Island-wide foliage lean: most islands stay green, a few run warm
  // (autumn) or cool. Deterministic per island id. Themed biomes (M18a)
  // pin the lean so their vegetation reads in-theme (snow pines never
  // turn orange; autumn islands always do).
  const leanDraw = hash01(island.id, 1, island.id * 7, 71);
  const BIOME_LEAN: Record<string, number> = {
    winter: 0.05,
    spooky: 0.0,
    tropical: 0.03,
    autumn: -0.14 - (leanDraw - 0.5) * 0.06,
  };
  const islandLean = BIOME_LEAN[island.biome] ?? (leanDraw < 0.6
    ? (leanDraw - 0.3) * 0.05 // subtle green spread
    : leanDraw < 0.85
    ? -0.12 - (leanDraw - 0.6) * 0.3 // autumn arc (toward orange)
    : 0.06); // cool teal-leaning

  const meshes: THREE.InstancedMesh[] = [];
  const cloned: THREE.Material[] = [];

  for (const [pieceId, placements] of byPiece) {
    const template = assets.templates.get(pieceId);
    if (!template) {
      missing.add(pieceId);
      continue;
    }
    const isBlock = pieceId.includes("cliff_block");
    // Block mass families (Platformer voxels): occluders like cliff
    // masses, but their tops/sides are PRE-TEXTURED — value jitter only,
    // never a hue tint (a green multiply over snow reads mossy).
    const isMassBlock = pieceId.startsWith("platformer-kit:block-");
    const isCap = !isBlock && !isMassBlock && pieceId.includes("ground_");
    for (const sub of subMeshesOf(template)) {
      const mat = sub.material.clone();
      // Cap tiles lie EXACTLY coplanar with the cliff-block tops beneath
      // them — polygon offset pulls the cap toward the camera so the
      // pair never z-fights (the distance shimmer on island tops).
      if (isCap) {
        mat.polygonOffset = true;
        mat.polygonOffsetFactor = -1;
        mat.polygonOffsetUnits = -1;
      }
      cloned.push(mat);
      const imesh = new THREE.InstancedMesh(
        sub.geometry,
        mat,
        placements.length,
      );
      imesh.castShadow = true;
      imesh.receiveShadow = true;
      const anim: AnimInstances = { base: [], worldPos: [] };

      placements.forEach((p, i) => {
        const s = p.scale ?? 1;
        tmpP.set(p.pos[0], p.pos[1], p.pos[2]);
        tmpQ.setFromAxisAngle(
          new THREE.Vector3(0, 1, 0),
          p.rotation * Math.PI / 2,
        );
        tmpS.set(s, s, s);
        tmpM.compose(tmpP, tmpQ, tmpS);
        tmpM.multiply(sub.local);
        imesh.setMatrixAt(i, tmpM);
        anim.base.push(tmpM.clone());
        anim.worldPos.push(tmpP.clone());

        const color = isBlock
          ? blockColor(
            island.biome,
            island.topY - p.pos[1],
            p.pos[0],
            p.pos[1],
            p.pos[2],
          )
          : isMassBlock
          ? tmpC.setHSL(
            0,
            0,
            clamp(0.92 + (hash01(p.pos[0], p.pos[1], p.pos[2], 131) - 0.5) *
              0.12, 0.8, 1),
          ).clone()
          : isCap
          ? capColor(island.biome, p.pos[0], p.pos[2])
          : decorColor(islandLean, sub.foliage, p.pos[0], p.pos[1], p.pos[2]);
        imesh.setColorAt(i, color);
      });
      imesh.instanceMatrix.needsUpdate = true;
      if (imesh.instanceColor) imesh.instanceColor.needsUpdate = true;
      imesh.computeBoundingSphere();
      imesh.userData.animInstances = anim;
      // Camera occluders are the MASSES only (cliff blocks + caps) —
      // pulling the boom in for a tree frond crossing the sightline
      // read as a camera jump (user report).
      imesh.userData.occluder = isBlock || isMassBlock || isCap;
      group.add(imesh);
      meshes.push(imesh);
    }
  }

  return {
    meshes,
    dispose() {
      for (const m of meshes) m.dispose(); // instance buffers only
      for (const m of cloned) m.dispose();
    },
  };
}
